import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  inject,
} from '@angular/core';
import {
  BehaviorSubject,
  Subscription,
  of,
  Subject,
  Observable,
  from,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  tap,
  switchMap,
  catchError,
  map,
} from 'rxjs/operators';
import { withMatchedEan } from '../../../core/utils/current-ean.util';
import { ActivatedRoute, Router } from '@angular/router'; // 🟢 FIX 1: Import Router
import { ToastrService } from 'ngx-toastr';
import { StockTakeService } from '../stock-take.service';
import { environment } from '../../../../environments/environment';
import { ExcelUploadComponent } from '../../../../shared/excel-upload/excel-upload.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';

import { ProductsService } from '../../products/products.service';

import { CanComponentDeactivate } from '../../../../Guard/pending-changes.guard';
import {
  WarehouseSelectComponent,
  WarehouseOption,
} from '../../../shared/components/warehouse-select/warehouse-select.component';

// --- Interfaces for Product/Variant Selection ---
interface Variant {
  id: string; // This is the variantId
  sku: string; // The SKU from the product/variant
  variantName: string; // For display
}

interface SearchProduct {
  id: string;
  sku: string;
  name: string;
  variants: Variant[]; // Array of variants
  /** Set by the "Search by EAN" box: the code the typed term matched. */
  matchedEan?: string;
}

interface DraftItemPayload {
  sku: string;
  variantId: string;
  newQty: number;
}
// ---------------------------------------------------

@Component({
  selector: 'app-stock-take-create',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    ExcelUploadComponent,
    RouterModule,
    NgSelectModule,
    WarehouseSelectComponent,
  ],
  templateUrl: './create-stock-take.component.html',
  styleUrls: ['./create-stock-take.component.scss'],
})
export class StockTakeCreateComponent
  implements OnInit, OnDestroy, AfterViewChecked, CanComponentDeactivate
{
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  title = 'Create Stock Take';
  uploadType: 'full' | 'partial' = 'full';
  uploadUrl = '';
  isViewMode = false;
  isLoadingDraftItems = false;

  // The store/warehouse this stock take is counted at — resolved from the
  // header switcher (the single place the working location is chosen), not
  // from a picker on this screen.
  selectedStoreId: string | null = null;
  selectedStoreName: string | null = null;
  selectedStoreCode: string | null = null;

  // Store this draft belongs to, resolved from the draft response so it can be
  // shown on the edit/preview screen (the store picker is gone by then).
  draftStoreName: string | null = null;
  draftStoreCode: string | null = null;
  // Draft mode (FULL / PARTIAL), shown alongside the store on the edit screen.
  draftMode: string | null = null;

  // 🟢 NEW: State flags for the new buttons
  isSaving: boolean = false;
  isSubmitting: boolean = false;

  stockTakeData: any[] | null = null;
  pagedData: any[] = [];

  searchText = '';
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;

  private maxPagesToShow = 5;

  private storeId: string | null = null;
  private currentDraftId: string | null = null;
  private readonly API_BASE_URL = `${environment.apiUrl}/stock-take-draft`;

  private searchSubject = new BehaviorSubject<string>('');
  private searchSubscription!: Subscription;
  private shouldFocusSearch = false;
  private confirmDialog = inject(ConfirmDialogService);

  // Flag to track if the draft has been modified since the last save/load
  hasUnsavedChanges: boolean = false;

  isSearchingSku = false;
  isSearchingEan = false;
  isSearchingName = false;
  productSearchResults: SearchProduct[] = [];
  isSearchingProducts = false;
  selectedBaseProduct: SearchProduct | null = null;
  productVariants: Variant[] = [];
  selectedVariant: Variant | null = null;
  quantityToAdd: number = 0;
  public productSearch$ = new Subject<string>();
  private productSearchSubscription!: Subscription;
  public skuSearchSubject = new Subject<string>();
  public eanSearchSubject = new Subject<string>();
  public nameSearchSubject = new Subject<string>();

  // -------------------------------------------

  constructor(
    private toastr: ToastrService,
    private stockTakeService: StockTakeService,
    private route: ActivatedRoute,
    private router: Router, // 🟢 FIX 2: Inject Router
    private productService: ProductsService
  ) {}

  ngOnInit(): void {
    this.storeId = this.getStoreIdFromLocalStorage();
    this.setupSearchDebounce();
    this.setupProductSearch();

    // Editing or viewing an existing draft keeps its own store context
    // untouched (the header-driven field only renders in the create flow).
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          this.currentDraftId = params.get('id');
          const queryParams = this.route.snapshot.queryParams;
          this.isViewMode = queryParams['view'] === 'true';

          if (this.isViewMode) {
            this.title = 'View Stock Take Draft';
            this.toastr.info(
              'You are in view mode. Actions are disabled.',
              'Preview'
            );
          } else if (this.currentDraftId) {
            this.title = 'Edit Stock Take Draft';
          }

          if (this.currentDraftId) this.isLoadingDraftItems = true;

          return of(null);
        })
      )
      .subscribe(() => {
        if (this.currentDraftId) {
          this.searchSubject.next(this.searchText.trim());
        }
      });

    // In the create flow the store/warehouse comes from the header switcher
    // via <app-warehouse-select>, which emits on init (see onWarehouseChange).
    // The upload URL is only built once it resolves (see updateUploadType).
  }

  ngAfterViewChecked(): void {
    if (this.shouldFocusSearch && this.searchInputRef?.nativeElement) {
      this.searchInputRef.nativeElement.focus();
      this.shouldFocusSearch = false;
    }
  }

  // -------------------------------------------------------------------
  // CAN DEACTIVATE GUARD LOGIC
  // -------------------------------------------------------------------

  canDeactivate(): boolean | Observable<boolean> {
    console.log(`[CanDeactivate] Checking for unsaved changes...`);

    if (this.isViewMode || !this.hasUnsavedChanges) {
      return true;
    }

    return from(
      this.confirmDialog.confirm({
        title: 'Discard Stock Take Changes?',
        message:
          'You have unsaved changes in the **Stock Take Draft**. Are you sure you want to leave this page? ',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      })
    );
  }
  // -------------------------------------------------------------------
  // PRODUCT SEARCH LOGIC
  // -------------------------------------------------------------------
  setupProductSearch(): void {
    // Helper function to handle the actual API call logic
    const handleSearch = (
      term: string,
      mode: 'sku' | 'ean' | 'query',
    ): Observable<{ data: any[]; total: number }> => {
      if (term?.length > 2 && this.storeId) {
        // Set the correct loading flag
        this.isSearchingSku = mode === 'sku';
        this.isSearchingEan = mode === 'ean';
        this.isSearchingName = mode === 'query';

        return this.productService
          .searchProducts(term, mode, this.storeId)
          .pipe(
            // EAN hits carry the matched code for the option label.
            map((response: any) =>
              mode === 'ean'
                ? { ...response, data: withMatchedEan(response?.data || [], term) }
                : response,
            ),
            catchError(() => {
              this.toastr.error(`Failed to search products by ${mode}.`);
              return of({ data: [], total: 0 });
            })
          );
      }

      this.isSearchingSku = false;
      this.isSearchingEan = false;
      this.isSearchingName = false;
      return of({ data: [], total: 0 });
    };

    // Subscription for SKU Search
    const skuSub = this.skuSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => handleSearch(term, 'sku')) // Use 'sku' mode
      )
      .subscribe((response: { data: any; total: number }) => {
        this.productSearchResults = response.data || [];
        this.isSearchingSku = false;
      });

    // Subscription for EAN Search
    const eanSub = this.eanSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => handleSearch(term, 'ean'))
      )
      .subscribe((response: { data: any; total: number }) => {
        this.productSearchResults = response.data || [];
        this.isSearchingEan = false;
      });

    // Subscription for Product Name Search
    const nameSub = this.nameSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => handleSearch(term, 'query')) // Use 'query' (name) mode
      )
      .subscribe((response: { data: any; total: number }) => {
        this.productSearchResults = response.data || [];
        this.isSearchingName = false;
      });

    // Combine subscriptions for clean teardown
    this.productSearchSubscription = new Subscription();
    this.productSearchSubscription.add(skuSub);
    this.productSearchSubscription.add(eanSub);
    this.productSearchSubscription.add(nameSub);
  }

  clearSearch(currentMode: 'sku' | 'ean' | 'name'): void {
    if (this.selectedBaseProduct) {
      // Keep results if an item is selected
      return;
    }

    // Clear results if the user blurred without selecting an item
    this.productSearchResults = [];

    if (currentMode === 'sku') {
      this.isSearchingSku = false;
    } else if (currentMode === 'ean') {
      this.isSearchingEan = false;
    } else {
      this.isSearchingName = false;
    }
  }

  onProductSelected(result: any): void {
    // 1. Reset dependent state
    this.selectedVariant = null;
    this.productVariants = [];
    this.quantityToAdd = 1;

    // 2. CHECK: Ensure the incoming result is not null
    if (result) {
      this.selectedBaseProduct = result;

      // 3. VARIANT HANDLING:
      const variants = this.selectedBaseProduct?.variants;

      if (variants && variants.length > 0) {
        this.productVariants = variants;

        // Auto-select if there's only one variant
        if (this.productVariants.length === 1) {
          this.selectedVariant = this.productVariants[0];
        }
      } else {
        this.toastr.warning('The selected product has no variants.');
      }
    } else {
      // If result is null (e.g., cleared the select box), ensure state is fully reset
      this.selectedBaseProduct = null;
    }
  }

  onVariantSelected(variant: Variant | null): void {
    this.selectedVariant = variant;
  }

  // -------------------------------------------------------------------

  setupSearchDebounce(): void {
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchQuery) => {
          this.currentPage = 1;
          this.loadDraftItemsPage(
            this.currentPage,
            this.itemsPerPage,
            searchQuery
          );
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.productSearchSubscription?.unsubscribe();
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchText.trim());
    this.shouldFocusSearch = true;
  }

  addProductToDraft(): void {
    if (
      this.isViewMode ||
      !this.currentDraftId ||
      !this.selectedBaseProduct?.variants[0].id ||
      this.quantityToAdd <= 0
    ) {
      this.toastr.error(
        'Draft is not initialized, or a product variant/quantity is missing.'
      );
      return;
    }

    const variantIdToAdd = this.selectedBaseProduct.variants[0].id;

    // 2. Check for Duplicate Item in *Currently Loaded* Data
    const existingItem = this.stockTakeData?.find(
      (item) => item.variantId === variantIdToAdd
    );
    if (existingItem) {
      const variantName =
        this.selectedBaseProduct.variants[0].variantName ||
        this.selectedBaseProduct.name ||
        'Item';
      this.toastr.warning(
        `Variant ${variantName} is already in the list. Please update its quantity directly.`
      );
      return;
    }

    const itemToAdd: DraftItemPayload = {
      sku: this.selectedBaseProduct.sku,
      variantId: variantIdToAdd,
      newQty: this.quantityToAdd,
    };

    this.stockTakeService
      .addDraftItem(this.currentDraftId, itemToAdd)
      .subscribe({
        next: () => {
          this.toastr.success(`Item ${itemToAdd.sku} added to draft. 🚀`);

          // FIX 3: Reset flag because the new item was successfully saved to the server
          this.hasUnsavedChanges = false;

          // Reset selection state
          this.selectedBaseProduct = null;
          this.selectedVariant = null;
          this.productVariants = [];
          this.quantityToAdd = 1;

          let targetPage = this.currentPage;

          if (this.searchText.trim()) {
            targetPage = 1;
          } else {
            const newTotalItems = this.totalItems + 1;
            const newTotalPages = Math.ceil(newTotalItems / this.itemsPerPage);
            targetPage = newTotalPages;
          }

          this.loadDraftItemsPage(
            targetPage,
            this.itemsPerPage,
            this.searchText
          );
        },
        error: (err: any) => {
          this.toastr.error(
            err.error?.message || 'Failed to add item to draft.'
          );
        },
      });
  }
  // -------------------------------------------------------------------
  loadDraftItemsPage(page: number, limit: number, search: string): void {
    if (!this.currentDraftId) return;
    this.isLoadingDraftItems = true;

    this.stockTakeService
      .getDraftItemsPaginated(this.currentDraftId, page, limit, search)
      .subscribe({
        next: (response: any) => {
          this.isLoadingDraftItems = false;

          this.stockTakeData = response.data || response.items || [];
          this.pagedData = this.stockTakeData || [];
          this.totalItems = response.total || 0;
          this.currentPage = page;
          this.captureDraftStore(response);

          // FIX 4: Reset flag after successfully loading the draft items from the server
          this.hasUnsavedChanges = false;

          this.shouldFocusSearch = true;
        },
        error: (err) => {
          this.isLoadingDraftItems = false;
          this.toastr.error('Failed to load stock take items.');
          this.shouldFocusSearch = true;
        },
      });
  }

  onItemsPerPageChange(): void {
    this.currentPage = 1;
    this.loadDraftItemsPage(
      this.currentPage,
      this.itemsPerPage,
      this.searchText
    );
    this.shouldFocusSearch = true;
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.loadDraftItemsPage(page, this.itemsPerPage, this.searchText);
      this.shouldFocusSearch = true;
    }
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  get startPage(): number {
    if (this.totalPages <= this.maxPagesToShow) {
      return 2;
    }

    const pivot = Math.floor(this.maxPagesToShow / 2);
    let start = this.currentPage - pivot;
    start = Math.max(2, start);

    if (start > this.totalPages - this.maxPagesToShow + 1) {
      start = this.totalPages - this.maxPagesToShow + 1;
    }

    return Math.max(2, start);
  }

  get endPage(): number {
    if (this.totalPages <= this.maxPagesToShow) {
      return this.totalPages - 1;
    }

    let end = this.startPage + this.maxPagesToShow - 1;
    end = Math.min(this.totalPages - 1, end);

    return end;
  }

  // -------------------------------------------------------------------
  // ITEM QUANTITY AND ACTION HANDLERS
  // -------------------------------------------------------------------

  onQtyChange(item: any): void {
    if (this.isViewMode) return;
    // No Math.max(0, ...) here: system stock can be negative and a count of a
    // negative position must stay negative — clamping silently turned -2 into 0.
    item.existingQty = Number(item.existingQty) || 0;
    item.newQty = Number(item.newQty) || 0;
    item.varianceQty = item.newQty - item.existingQty;

    // CORRECT: Set flag true because the item quantity has been locally changed
    this.hasUnsavedChanges = true;
  }

  deleteRow(item: any) {
    if (this.isViewMode) return;
    const draftId = this.currentDraftId;
    if (!draftId) {
      this.toastr.error('Draft ID not found. Cannot delete item.');
      return;
    }

    if (confirm(`Are you sure you want to delete SKU: ${item.sku}?`)) {
      this.stockTakeService.deleteDraftItem(draftId, item.id).subscribe({
        next: () => {
          this.toastr.success(`Item ${item.sku} deleted successfully!`);

          // CORRECT: Set flag true because an item was deleted
          this.hasUnsavedChanges = true;

          this.loadDraftItemsPage(
            this.currentPage,
            this.itemsPerPage,
            this.searchText
          );
        },
        error: (err: any) => {
          this.toastr.error(`Failed to delete item ${item.sku}.`);
        },
      });
    }
  }

  updateItem(item: any) {
    if (this.isViewMode) return;
    const draftId = this.currentDraftId;
    if (!draftId) {
      this.toastr.error('Draft ID not found. Cannot update item.');
      return;
    }

    this.stockTakeService
      .updateDraftItemQty(draftId, item.id, {
        newQty: Number(item.newQty),
      })
      .subscribe({
        next: () => {
          this.toastr.success(`Item ${item.sku} quantity updated successfully`);

          // CORRECT: Reset flag to false because the item quantity change was committed to the server
          this.hasUnsavedChanges = false;

          this.loadDraftItemsPage(
            this.currentPage,
            this.itemsPerPage,
            this.searchText
          );
        },
        error: (err: any) =>
          this.toastr.error(`Failed to update item ${item.sku}`),
      });
  }

  // -------------------------------------------------------------------
  // 🟢 NEW: SAVE AND SUBMIT HANDLERS
  // -------------------------------------------------------------------

  /**
   * Saves the draft's metadata (notes) and keeps the draft open.
   */
  saveDraft(): void {
    if (this.isSaving || !this.currentDraftId) return;

    this.isSaving = true;
    // NOTE: Replace '' with this.draftNotes if you add a notes input field
    const notes = '';

    this.stockTakeService.saveDraft(this.currentDraftId).subscribe({
      next: () => {
        this.toastr.success('Draft successfully saved!', 'Saved');
        this.isSaving = false;
        setTimeout(() => this.router.navigate(['/stocktake']), 500);
        // The hasUnsavedChanges flag remains true if local item changes haven't been updated via updateItem
      },
      error: (err) => {
        this.toastr.error('Failed to save draft.', 'Error');
        this.isSaving = false;
      },
    });
  }

  /**
   * Finalizes the stock take, preventing further edits, and applies changes to inventory.
   */
  async submitDraft(): Promise<void> {
    if (this.isSubmitting || !this.currentDraftId) return;

    // Check for uncommitted quantity changes
    if (this.hasUnsavedChanges) {
      this.toastr.warning(
        'Please update all modified items before submitting.',
        'Unsaved Changes'
      );
      return;
    }

    this.isSubmitting = true;

    this.stockTakeService.submitDraft(this.currentDraftId as string).subscribe({
      next: () => {
        this.toastr.success(
          'Stock take successfully submitted and inventory updated!',
          'Submitted'
        );
        this.isSubmitting = false;

        // Delay navigation slightly for better UX
        setTimeout(() => this.router.navigate(['/stocktake']), 500);
      },
      error: (err) => {
        this.toastr.error(
          err.error?.message || 'Failed to submit stock take.',
          'Error'
        );
        this.isSubmitting = false;
      },
    });
  }

  // -------------------------------------------------------------------
  // UPLOAD AND UTILITY HANDLERS
  // -------------------------------------------------------------------

  onUploadSuccess(response: any) {
    if (this.isViewMode) return;

    const newDraftId =
      response.draftId ||
      response.id ||
      response.body?.draftId ||
      response.body?.id;

    if (!newDraftId) {
      this.toastr.error(
        'Upload successful, but Draft ID was missing from the response.'
      );
      return;
    }

    this.currentDraftId = newDraftId;
    this.title = 'Edit Stock Take Draft';

    // Prefer the store echoed back by the upload; fall back to the store the
    // user just picked so the header shows context immediately.
    this.captureDraftStore(response.body || response);

    this.stockTakeData = response.body.data || response.data || [];
    this.totalItems = this.stockTakeData?.length || 0;
    this.currentPage = 1;
    this.pagedData = this.stockTakeData!;

    // CORRECT: Reset flag to false because the upload just created a fresh draft
    this.hasUnsavedChanges = false;

    this.loadDraftItemsPage(
      this.currentPage,
      this.itemsPerPage,
      this.searchText
    );

    this.toastr.success('Stocktake uploaded and draft created successfully!');
  }

  onUploadError(error: any) {
    console.error('Upload failed', error);
    this.toastr.error(error.error?.message || 'Failed to upload stocktake.');
  }

  getStoreIdFromLocalStorage(): string | null {
    try {
      const userString = localStorage.getItem('user');
      if (userString) {
        const user = JSON.parse(userString);
        // Prefer the store matching the ACTIVE location (header switcher) —
        // with a second warehouse, storeAccess[0] is an arbitrary row and
        // user.storeId a login-time guess. Multi-access users with no active
        // match get NO default and must pick explicitly.
        const activeLocationId = localStorage.getItem('activeLocationId');
        const access: any[] = Array.isArray(user.storeAccess)
          ? user.storeAccess
          : [];
        const activeMatch = access.find(
          (a: any) => a?.locationId === activeLocationId,
        );
        if (activeMatch?.storeId) return String(activeMatch.storeId);
        if (access.length === 1 && access[0]?.storeId)
          return String(access[0].storeId);
        return access.length === 0 ? user.storeId || null : null;
      }
      return null;
    } catch {
      return null;
    }
  }

  updateUploadType(type: 'full' | 'partial') {
    this.uploadType = type;
    // The upload endpoint bakes the store into the URL. Build it only once a
    // store is chosen, so a file can never be uploaded against the wrong store.
    this.uploadUrl = this.selectedStoreId
      ? `${this.API_BASE_URL}/upload/${type}/${this.selectedStoreId}`
      : '';
  }

  /**
   * The header switcher decided which store/warehouse we are counting at:
   * re-point the upload URL and the product search scope, and drop any stale
   * search results from the previous one.
   */
  onWarehouseChange(warehouse: WarehouseOption | null): void {
    this.selectedStoreId = warehouse?.storeId ?? null;
    this.storeId = this.selectedStoreId;
    this.selectedStoreName = warehouse?.name ?? null;
    this.selectedStoreCode = warehouse?.storeCode ?? null;
    this.productSearchResults = [];
    this.updateUploadType(this.uploadType);
  }

  /**
   * Record the draft's store from a backend response (upload or items page)
   * so the edit/preview header can show which store this stock take is for.
   */
  private captureDraftStore(source: any): void {
    if (!source) return;
    if (source.storeName) this.draftStoreName = source.storeName;
    if (source.storeCode) this.draftStoreCode = source.storeCode;
    if (source.mode) this.draftMode = source.mode;

    // Fallback for the instant after upload, before the store name is echoed:
    // use the store/warehouse the header context resolved to.
    if (!this.draftStoreName && this.selectedStoreId) {
      this.draftStoreName = this.selectedStoreName;
      this.draftStoreCode = this.selectedStoreCode;
    }
  }

  /** Human-friendly "Store Name (CODE)" for the header; null when unknown. */
  get storeContextLabel(): string | null {
    if (!this.draftStoreName) return null;
    return this.draftStoreCode
      ? `${this.draftStoreName} (${this.draftStoreCode})`
      : this.draftStoreName;
  }

  /** "Full" / "Partial" label for the draft mode; null when unknown. */
  get stockTakeModeLabel(): string | null {
    if (!this.draftMode) return null;
    return this.draftMode.toUpperCase() === 'PARTIAL' ? 'Partial' : 'Full';
  }

  // ── Line grid helpers (Zenflow kit: header card, variance pills, totals) ──
  // Lines are paged server-side, so pills / totals cover the loaded page.
  headerOpen = true;
  lineFilter: 'all' | 'short' | 'over' | 'match' = 'all';

  /** short = counted below SOH, over = above, match = equal. */
  lineState(item: any): 'short' | 'over' | 'match' {
    const v = Number(item?.varianceQty || 0);
    return v < 0 ? 'short' : v > 0 ? 'over' : 'match';
  }

  /** Rows of the current page after the All / Short / Over / Matching pills. */
  get visibleStockLines(): { item: any; index: number }[] {
    const out: { item: any; index: number }[] = [];
    (this.pagedData || []).forEach((item, index) => {
      if (this.lineFilter !== 'all' && this.lineState(item) !== this.lineFilter) return;
      out.push({ item, index });
    });
    return out;
  }

  get shortLineCount(): number {
    return (this.pagedData || []).filter((i) => this.lineState(i) === 'short').length;
  }

  get overLineCount(): number {
    return (this.pagedData || []).filter((i) => this.lineState(i) === 'over').length;
  }

  get matchLineCount(): number {
    return (this.pagedData || []).filter((i) => this.lineState(i) === 'match').length;
  }

  get stockLineCountLabel(): string {
    const all = this.totalItems || 0;
    const page = (this.pagedData || []).length;
    const shown = this.visibleStockLines.length;
    if (!page) return `${all} ${all === 1 ? 'line' : 'lines'}`;
    if (shown !== page) return `${shown} of ${page} on this page`;
    return page === all ? `${all} ${all === 1 ? 'line' : 'lines'}` : `${page} of ${all} lines`;
  }

  get pageSohTotal(): number {
    return (this.pagedData || []).reduce((n, i) => n + Number(i?.existingQty || 0), 0);
  }

  get pageNewTotal(): number {
    return (this.pagedData || []).reduce((n, i) => n + Number(i?.newQty || 0), 0);
  }

  get pageVarianceTotal(): number {
    return (this.pagedData || []).reduce((n, i) => n + Number(i?.varianceQty || 0), 0);
  }

  /** Column count of the counting sheet (the Action column hides in view mode). */
  get sheetColCount(): number {
    return this.isViewMode ? 6 : 7;
  }

  clearLineFilters(): void {
    this.lineFilter = 'all';
  }
}
